import type { SiteConfig } from './types';

const CDN_BASE = 'https://cdn.babyvogue.bg/assets';

export const babyVogueConfig: SiteConfig = {
  id: 'babyvogue',

  // ---------------------------------------------------------------------------
  // ТЕМА – меко градинско/тюркоазено зелено (вторият цвят от логото, #6a857c).
  // Светлите нюанси (50–300) са тинтове за фонове/рамки; функционалните 500–900
  // са по-тъмни, за да са ЧЕТИМИ като „мастило" (текст/бутони/икони) на светъл фон.
  // Топлите пастелни кръгове на фона (--dynamic-color) остават и допълват зеленото.
  // ---------------------------------------------------------------------------
  theme: {
    primary50: '#eef4f1',
    primary100: '#dde9e4',
    primary200: '#c0d3cb',
    primary300: '#9fb9af',
    primary400: '#7e9c91', // светъл вариант на брандовото зелено
    primary500: '#5f7a71', // Main brand ink – #6a857c, леко по-тъмен за четимост (WCAG AA)
    primary600: '#4e6860', // бутони (бял текст)
    primary700: '#3e534d',
    primary800: '#2f3f3a',
    primary900: '#202b28',

    primaryGlow: 'rgba(106, 133, 124, 0.30)',
    accentColor: 'hsl(39, 70%, 85%)', // светъл топъл – ползва се само като --dynamic-color (мек фон)
    themeColor: '#5f7a71', // брандово зелено за browser/PWA лента

    // Златистото от логото (#e9b667) — по него BabyVogue се различава на пръв
    // поглед. Мастилото е тъмно: бяло върху този тон е под 2:1.
    switchAccent: '#e9b667',
    switchAccentDeep: '#d79f4b',
    switchAccentInk: '#2d2d2d',

    backgroundPalette: [
      'hsl(39, 65%, 78%)', // златно
      'hsl(25, 60%, 78%)', // топла праскова
      'hsl(48, 55%, 80%)', // кремаво жълто
      'hsl(15, 55%, 80%)', // корал
      'hsl(35, 60%, 76%)', // меден
      'hsl(50, 50%, 82%)', // бледожълто
      'hsl(30, 55%, 78%)', // бежово-златно
      'hsl(20, 50%, 80%)', // топла праскова
      'hsl(45, 55%, 80%)', // златиста бежова
      'hsl(10, 50%, 80%)', // праскова-корал
    ],
    circleBig: { size: 1000, opacity: 0.5 },
    circleSmall: { size: 500, opacity: 0.15 },
  },

  // ---------------------------------------------------------------------------
  // БРАНДИНГ
  // ---------------------------------------------------------------------------
  branding: {
    name: 'BabyVogue',
    tagline: 'Запазете първите спомени завинаги',
    description:
      'Запазете най-хубавите моменти от детството в стилна персонализирана фотокнига. Лесно качете снимки, подредете страниците и създайте уникален спомен за цялото семейство.',
    keywords:
      'детска фотокнига, детски албум, фотокнига за деца, албум със снимки, детски спомени, персонализиран албум, photo book kids, family photo album',
    locale: 'bg_BG',
    lang: 'bg',

    cdnBase: 'https://cdn.babyvogue.bg',

    logoUrl: `${CDN_BASE}/logos/babyvogue-logo.svg`,
    logoMobileUrl: `${CDN_BASE}/logos/babyvogue-logo.svg`,
    // Логото на гръбчето – същите имена като на travogue.
    logoMobileDarkUrl: `${CDN_BASE}/logos/logo-mobile.svg`,
    logoMobileLightUrl: `${CDN_BASE}/logos/logo-mobile-light.svg`,
    logoAlt: 'BabyVogue Logo',

    // Качени в assets/favicons/ (както travogue); старият assets/babyvogue/ път бе 404.
    pwaIcon192: `${CDN_BASE}/favicons/web-app-manifest-192x192.png`,
    pwaIcon512: `${CDN_BASE}/favicons/web-app-manifest-512x512.png`,
    pwaCategories: ['photo', 'baby', 'family', 'lifestyle'],

    faviconBundle: {
      // Файловете са качени в assets/favicons/ (както при travogue) — старият път
      // assets/babyvogue/ беше 404 → Google показваше генерична иконка в резултатите.
      png96: `${CDN_BASE}/favicons/favicon-96x96.png`,
      svg: `${CDN_BASE}/favicons/favicon.svg`,
      ico: `${CDN_BASE}/favicons/favicon.ico`,
      appleTouch180: `${CDN_BASE}/favicons/apple-touch-icon.png`,
      webManifest: '/api/manifest',
    },

    fontFamily: '"Oddval", sans-serif',
    fontFamilyName: 'Oddval',
  },

  // ---------------------------------------------------------------------------
  // КОНТАКТ
  // ---------------------------------------------------------------------------
  contact: {
    email: 'babyvoguebook@gmail.com',
    phone: '359897686203',
    socialFacebook: 'https://www.facebook.com/share/1CuPm8c7BC/?mibextid=wwXIfr',
    socialInstagram: 'https://www.instagram.com/babyvoguebook',
  },

  // ---------------------------------------------------------------------------
  // ДОМЕЙН
  // ---------------------------------------------------------------------------
  domain: {
    production: 'babyvogue.bg',
    staging: 'staging.babyvogue.bg',
    // www е каноничният хост (edge-ът 301-ва голия домейн към www) — sitemap,
    // canonical, robots и og:url се извеждат оттук и трябва да сочат www,
    // иначе всеки URL в sitemap-а минава през redirect (шум в GSC).
    metadataBase: 'https://www.babyvogue.bg',
    googleVerification: 'tDbsFYa2Pi37Kvu6Wf4Qcx1RGlkfmy65YwWu1uuafuY',
  },

  // ---------------------------------------------------------------------------
  // ФИРМЕНИ / ПРАВНИ ДАННИ
  // ---------------------------------------------------------------------------
  legal: {
    companyName: process.env.COMPANY_NAME || '',
    companyFullName: '„Процесинг Партнърс" ООД',
    eik: process.env.EIK || '',
    address: process.env.COMPANY_ADDRESS || '',
    legalEmail: 'info.processingpartners@gmail.com',
    legalPhone: '+359897686203',
    vatRegistered: process.env.VAT_REGISTERED === 'yes',
    vatRate: parseFloat(process.env.VAT_RATE || '20'),
  },

  // ---------------------------------------------------------------------------
  // ПЛАЩАНЕ (DSK)
  // ---------------------------------------------------------------------------
  payment: {
    // TODO: Смени с BabyVogue DSK credentials
    merchantLogin: process.env.NEXT_PUBLIC_DSK_USERNAME || process.env.DSK_USERNAME || '',
    merchantName: process.env.NEXT_PUBLIC_DSK_MERCHANT_NAME || 'BabyVogue',
    merchantId: '', // TODO: Apple Pay merchant ID
    currency: parseInt(process.env.DSK_CURRENCY || '978', 10),
  },

  // ---------------------------------------------------------------------------
  // СЪДЪРЖАНИЕ
  // ---------------------------------------------------------------------------
  content: {
    aboutIntroImageAlt: 'Персонализирани фотоалбуми от BabyVogue',
    copyrightYear: new Date().getFullYear(),
    deliveryDescription: 'Персонализирана книга',
    defaultCoverTextColor: '#e9b667',
    taxonomyNoun: 'категория',
    taxonomyNounPlural: 'категории',
    offlineTitle: 'Няма интернет връзка',
    offlineDescription: 'Моля, проверете интернет връзката си и опитайте отново.',
    offlineRetryButton: 'Опитай отново',
    aboutAssets: {
      introImage: `${CDN_BASE}/about%20us/general.png`,
      packagingImage: `${CDN_BASE}/about%20us/package1.png`,
      heroImage: `${CDN_BASE}/images/template.png`,
      stepImages: [
        `${CDN_BASE}/about%20us/step1.svg`,
        `${CDN_BASE}/about%20us/step2.svg`,
        `${CDN_BASE}/about%20us/step3.svg`,
        `${CDN_BASE}/about%20us/step4.svg`,
      ],
      featurePlaceholder: `${CDN_BASE}/images/template.png`,
      teamPlaceholder: `${CDN_BASE}/images/template.png`,
    },
  },

  // ---------------------------------------------------------------------------
  // SHARED ASSETS (fallback URLs за map, textures, icons)
  // ---------------------------------------------------------------------------
  assets: {
    mapFallback: `${CDN_BASE}/map/map.svg`,
    textureFallback: `${CDN_BASE}/textures/noise.webp`,
    shelfBackground: `${CDN_BASE}/backgrounds/shelf.png`,
    googleIcon: `${CDN_BASE}/icons/google-icon.svg`,
    facebookIcon: `${CDN_BASE}/icons/facebook-icon.svg`,
    ogImage: `${CDN_BASE}/og/og-image.png`,
  },
};
